import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AI_BG_IMAGE } from "@/lib/brand";
import { suggestDesign } from "@/lib/api";

const suggestions = [
  "Halloween party for kids",
  "1st birthday — pastel pink",
  "Baby shower, gender reveal",
  "Thanksgiving teacher gifts",
];

export default function AIDesigner() {
  const [occasion, setOccasion] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!occasion.trim()) {
      toast.error("Please describe the occasion first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const data = await suggestDesign({ occasion: occasion.trim(), notes: notes.trim() || null });
      setResult(data);
    } catch (err) {
      toast.error("The designer is momentarily away. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="designer"
      data-testid="ai-designer-section"
      className="relative py-24 md:py-36 px-6 md:px-12 lg:px-20 overflow-hidden"
    >
      <div
        className="absolute inset-0 -z-10 bg-cover bg-center"
        style={{ backgroundImage: `url(${AI_BG_IMAGE})` }}
      />
      <div className="absolute inset-0 -z-10 bg-[#F5DCD5]/55" />

      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#B37C7C] font-medium mb-5">
            <Sparkles size={14} />
            AI Cookie Designer
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#362C2C] leading-[1.05] mb-6">
            Tell us the occasion.
            <span className="block italic font-accent-serif text-[#B37C7C]">
              We'll help you dream it up.
            </span>
          </h2>
          <p className="text-[#5C5252] max-w-xl mx-auto leading-relaxed">
            Not sure what to order? Describe what you're celebrating in one line, and
            our little AI helper will suggest a theme, colors, and cookie shapes.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/55 backdrop-blur-2xl border border-white/70 shadow-[0_20px_60px_rgba(179,124,124,0.12)] rounded-2xl p-6 md:p-10"
          data-testid="ai-designer-form"
        >
          <label className="block text-sm text-[#5C5252] mb-3">
            Occasion
          </label>
          <input
            type="text"
            value={occasion}
            onChange={(e) => setOccasion(e.target.value)}
            placeholder="e.g. Spring garden bridal shower for 60 guests"
            className="w-full bg-transparent border-b border-[#EFE8E8] focus:border-[#B37C7C] outline-none px-0 py-3 text-lg md:text-xl font-serif-display text-[#362C2C] placeholder:text-[#5C5252]/50 transition-colors"
            data-testid="ai-designer-occasion-input"
          />

          <label className="block text-sm text-[#5C5252] mt-8 mb-3">
            Anything to add? (optional)
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Preferred colors, must-have motifs, dietary notes…"
            className="w-full bg-transparent border-b border-[#EFE8E8] focus:border-[#B37C7C] outline-none px-0 py-3 text-base text-[#362C2C] placeholder:text-[#5C5252]/50 transition-colors"
            data-testid="ai-designer-notes-input"
          />

          <div className="mt-8 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setOccasion(s)}
                className="text-xs px-3 py-1.5 rounded-full border border-[#EFE8E8] bg-white/60 text-[#5C5252] hover:border-[#B37C7C] hover:text-[#B37C7C] transition-all"
                data-testid={`ai-designer-suggestion-${s.slice(0, 6)}`}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-[#B37C7C] text-white px-7 py-3.5 rounded-md hover:bg-[#9A6565] hover:-translate-y-0.5 hover:shadow-lg shadow-[#B37C7C]/20 transition-all duration-300 tracking-wide text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="ai-designer-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sketching…
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Design my cookies
                </>
              )}
            </button>
            <span className="text-xs text-[#5C5252]/70">
              Takes about 10–20 seconds · includes a visual concept
            </span>
          </div>
        </form>

        {result && (
          <div
            className="mt-10 bg-white/75 backdrop-blur-2xl border border-white/80 rounded-2xl p-8 md:p-12 fade-up"
            data-testid="ai-designer-result"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-[#B37C7C] mb-4">
              Design Concept
            </div>
            <h3 className="font-serif-display text-3xl md:text-4xl text-[#362C2C] mb-6" data-testid="ai-result-theme">
              {result.theme}
            </h3>

            {result.image_b64 && (
              <div className="mb-8 rounded-xl overflow-hidden border border-[#EFE8E8] bg-[#F7EBEB]">
                <img
                  src={`data:${result.image_mime || "image/png"};base64,${result.image_b64}`}
                  alt={`AI-generated concept for ${result.theme}`}
                  className="w-full h-auto"
                  data-testid="ai-result-image"
                />
                <div className="px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-[#5C5252] bg-white/60 border-t border-[#EFE8E8]">
                  AI-generated concept · illustrative only
                </div>
              </div>
            )}

            <p className="text-[#5C5252] leading-relaxed mb-10 max-w-2xl" data-testid="ai-result-description">
              {result.description}
            </p>

            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#5C5252] mb-4">
                  Palette
                </div>
                <div className="flex flex-col gap-2" data-testid="ai-result-palette">
                  {(result.palette || []).map((c) => (
                    <div key={c} className="flex items-center gap-3 text-sm text-[#362C2C]">
                      <span className="w-3 h-3 rounded-full bg-[#B37C7C]/60 border border-[#EFE8E8]" />
                      <span className="font-serif-display text-lg italic">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#5C5252] mb-4">
                  Suggested shapes
                </div>
                <ul className="space-y-2 text-[#362C2C]" data-testid="ai-result-shapes">
                  {(result.shapes || []).map((s) => (
                    <li key={s} className="font-serif-display text-lg">
                      · {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-[#EFE8E8]">
              <a
                href="#order"
                onClick={() => {
                  try {
                    sessionStorage.setItem(
                      "sr_ai_design",
                      JSON.stringify(result)
                    );
                    window.dispatchEvent(new CustomEvent("sr-ai-design-attached"));
                  } catch (e) {
                    /* ignore */
                  }
                }}
                className="inline-flex items-center gap-2 bg-[#B37C7C] text-white px-6 py-3 rounded-md hover:bg-[#9A6565] transition-all text-sm tracking-wide"
                data-testid="ai-result-order-cta"
              >
                Attach this design to my order →
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
